import { Money } from '../Money';
import { spokenPeso } from '../spokenPeso';

export interface HBarItem {
  key: string;
  label: string;
  valueMinor: number;
  color: string; // a CSS color, usually var(--chart-n)
}

interface HBarListProps {
  items: HBarItem[];
  ariaLabel: string;
  onSelect?: (key: string) => void;
  /** Label on its own line, amount under it: for narrow tiles where both don't fit on one line. */
  stacked?: boolean;
}

const FOCUS =
  'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)]';

/** Horizontal bars with the label and amount always in text above each bar. */
export function HBarList({ items, ariaLabel, onSelect, stacked = false }: HBarListProps) {
  const max = Math.max(1, ...items.map((i) => i.valueMinor));
  return (
    <ul aria-label={ariaLabel} className="flex flex-col gap-1">
      {items.map((item) => {
        const pct = (Math.max(0, item.valueMinor) / max) * 100;
        const name = `${item.label}, ${spokenPeso(item.valueMinor)}`;
        const body = (
          <>
            <span
              className={
                stacked ? 'flex flex-col gap-0.5' : 'flex items-baseline justify-between gap-3'
              }
            >
              <span className="flex min-w-0 items-center gap-2 text-sm text-fg">
                <svg width="10" height="10" aria-hidden="true" className="shrink-0">
                  <circle cx="5" cy="5" r="5" fill={item.color} />
                </svg>
                <span className="truncate">{item.label}</span>
              </span>
              <Money minor={item.valueMinor} className="shrink-0 text-sm" />
            </span>
            <svg
              className="mt-1 block h-2 w-full"
              viewBox="0 0 100 2"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <rect x="0" y="0" width="100" height="2" rx="1" fill="var(--secondary)" />
              <rect x="0" y="0" width={pct} height="2" rx="1" fill={item.color} />
            </svg>
          </>
        );
        return (
          <li key={item.key}>
            {onSelect ? (
              <button
                type="button"
                aria-label={name}
                onClick={() => onSelect(item.key)}
                className={`block min-h-11 w-full rounded-xl px-2 py-2 text-left ${FOCUS}`}
              >
                {body}
              </button>
            ) : (
              <div role="group" aria-label={name} className="px-2 py-2">
                {body}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
